import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Input,
  InputGroup,
  Table,
  Button,
  DOMHelper,
  Stack,
  DateRangePicker,
  Tag,
  IconButton,
  Loader,
  Message,
  useToaster,
  Pagination,
  Modal
} from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import ExportIcon from '@rsuite/icons/Export';
import { MdFilterList, MdOutlineDeleteOutline } from 'react-icons/md';
import { Gear, Icon } from '@rsuite/icons';
import DrawerView from './DrawerView';
import { useDispatch, useSelector } from 'react-redux';
import { fetchClientSiteRequests } from '@/services/thirdPartySite.service';
import { deleteRequest } from '@/services/request.service';
import { getAdminUsers } from '../../../services/user.service';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import AuthorizationPopup from './AuthorizationPopup';
import { fetchSetting } from '@/services/setting.service';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const DataTable = () => {
  const dispatch = useDispatch();
  const { data, pagination, loading } = useSelector(state => state.thirdPartySite);
  const { data: users, loading: usersLoading } = useSelector(state => state.users);
  const toaster = useToaster();
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [showDrawer, setShowDrawer] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    userId: '',
    registrationNumber: '',
    status: []
  });
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    end.setHours(23, 59, 59, 999);
    return [start, end];
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showAuthorizationPopup, setShowAuthorizationPopup] = useState(false);
  const [settingsId, setSettingsId] = useState<string | undefined>();
  const [initialAutoAccept, setInitialAutoAccept] = useState([]);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(getAdminUsers());
    const fetchData = async () => {
      setLoadingSettings(true);
      try {
        const res = await fetchSetting('automation');
        if (res) {
          setSettingsId(res._id);
          setInitialAutoAccept(res.autoAccept || []);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        toaster.push(
          <Message type="error" duration={3000}>
            {t('Error fetching automation settings')}
          </Message>,
          { placement: 'topCenter' }
        );
      } finally {
        setLoadingSettings(false);
      }
    };
    fetchData();
  }, [dispatch, t]);

  const handleChangeLimit = dataKey => {
    setPage(1);
    setLimit(dataKey);
  };

  const daysRequested = array => {
    if (!array) return '';
    var ch = '';
    array.map(operation => {
      ch +=
        operation.date.substring(0, 10).split('-').reverse().join('-') +
        ' ' +
        operation.timeslot +
        ' ';
    });
    return ch;
  };

  const getStatusColor = status => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return 'yellow';
      case 'ACCEPTED':
        return 'green';
      case 'REJECTED':
        return 'red';
      default:
        return 'gray';
    }
  };

  const applyFilters = (dataToFilter, searchTerm, currentFilters) => {
    let filtered = [...dataToFilter];

    if (searchTerm.trim()) {
      const searchStr = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        item =>
          `${item.idSender?.firstname || ''} ${item.idSender?.lastname || ''}`
            .toLowerCase()
            .includes(searchStr) ||
          (item.idSender?.registrationNumber || '').toLowerCase().includes(searchStr) ||
          (item.type || '').toLowerCase().includes(searchStr) ||
          (item._id?.status || '').toLowerCase().includes(searchStr)
      );
    }

    if (currentFilters.userId) {
      filtered = filtered.filter(item => item.idSender?._id === currentFilters.userId);
    }

    if (currentFilters.registrationNumber) {
      filtered = filtered.filter(item =>
        (item.idSender?.registrationNumber || '')
          .toLowerCase()
          .includes(currentFilters.registrationNumber.toLowerCase())
      );
    }

    if (currentFilters.status && currentFilters.status.length > 0) {
      filtered = filtered.filter(item =>
        currentFilters.status.includes(item._id?.status?.toLowerCase())
      );
    }

    return filtered;
  };

  useEffect(() => {
    const newFilteredData = applyFilters(data, searchKeyword, filters);
    setFilteredData(newFilteredData);
  }, [data, searchKeyword, filters]);

  useEffect(() => {
    if (dateRange && dateRange[0]) {
      const startDate = new Date(dateRange[0]);
      startDate.setHours(0, 0, 0, 0);

      const endDate = dateRange[1] ? new Date(dateRange[1]) : new Date(dateRange[0]);
      endDate.setHours(23, 59, 59, 999);

      dispatch(
        fetchClientSiteRequests(
          format(startDate, 'yyyy-MM-dd'),
          format(endDate, 'yyyy-MM-dd'),
          page,
          limit
        )
      );
    }
  }, [dateRange, page, limit, dispatch]);

  const handleSearch = searchTerm => {
    setSearchKeyword(searchTerm);
    setPage(1);
  };

  const handleFiltersChange = newFilters => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      userId: '',
      registrationNumber: '',
      status: []
    });
    setPage(1);
  };

  const downloadExcel = (data, name) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `${name}.xlsx`);
  };

  const handleExport = () => {
    const thirdPartySiteRequestsToExport = filteredData.map(item => {
      return {
        'Full Name': `${item.idSender?.firstname || ''} ${item.idSender?.lastname || ''}`,
        'Registration Number': item.idSender?.registrationNumber || '',
        'Days Requested': daysRequested(item.operations),
        Status: item._id?.status || '',
        'Created At': new Date(item._id?.createdAt).toLocaleString()
      };
    });

    downloadExcel(thirdPartySiteRequestsToExport, 'third-party-site-requests');
  };

  const handleDelete = rowData => {
    setSelectedRequest(rowData);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const requestId = selectedRequest._id?._id || selectedRequest._id;
      await dispatch(deleteRequest(requestId));

      toaster.push(
        <Message type="success" duration={3000}>
          {t('Third-party site request deleted successfully!')}
        </Message>,
        { placement: 'topCenter' }
      );
      setShowDeleteModal(false);

      if (dateRange && dateRange[0]) {
        const startDate = new Date(dateRange[0]);
        startDate.setHours(0, 0, 0, 0);
        const endDate = dateRange[1] ? new Date(dateRange[1]) : new Date(dateRange[0]);
        endDate.setHours(23, 59, 59, 999);

        dispatch(
          fetchClientSiteRequests(
            format(startDate, 'yyyy-MM-dd'),
            format(endDate, 'yyyy-MM-dd'),
            page,
            limit
          )
        );
      }
    } catch (error) {
      console.error('Delete Error:', error);
      toaster.push(
        <Message type="error" duration={3000}>
          {t('Error deleting third-party site request')}
        </Message>,
        { placement: 'topCenter' }
      );
      setShowDeleteModal(false);
    }
  };

  return (
    <>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            placeholder="Date Range"
            ranges={[
              {
                label: t('Last 30 Days'),
                value: [new Date(new Date().setDate(new Date().getDate() - 30)), new Date()]
              },
              {
                label: t('Last 7 Days'),
                value: [new Date(new Date().setDate(new Date().getDate() - 7)), new Date()]
              },
              {
                label: t('Today'),
                value: [new Date(new Date().setDate(new Date().getDate())), new Date()]
              }
            ]}
          />
        </Stack>

        <Stack spacing={6}>
          <InputGroup inside>
            <Input placeholder={t('Search')} value={searchKeyword} onChange={handleSearch} />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
          <Button appearance="primary" onClick={() => setShowDrawer(true)}>
            <Icon as={MdFilterList} style={{ marginRight: 8 }} /> {t('Filters')}
          </Button>
          <Button appearance="primary" onClick={handleExport}>
            <ExportIcon style={{ marginRight: 8 }} /> {t('Export')}
          </Button>

          <Button
            appearance="primary"
            onClick={() => setShowAuthorizationPopup(true)}
            disabled={!settingsId || loadingSettings}
          >
            <Gear style={{ marginRight: 8 }} /> {t('Settings')}
          </Button>
        </Stack>
      </Stack>
      <>
        <Table
          height={Math.max(getHeight(window) - 200, 400)}
          data={filteredData}
          loading={loading}
        >
          <Column flexGrow={4}>
            <HeaderCell>{t('First Name & Last Name')}</HeaderCell>
            <Cell>
              {rowData =>
                `${rowData.idSender?.firstname || ''} ${rowData.idSender?.lastname || ''}`
              }
            </Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('Registration Number')}</HeaderCell>
            <Cell>{rowData => rowData.idSender?.registrationNumber}</Cell>
          </Column>

          <Column flexGrow={4}>
            <HeaderCell>{t('Days Requested')}</HeaderCell>
            <Cell style={{ whiteSpace: 'pre-line' }}>
              {rowData => daysRequested(rowData.operations)}
            </Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('Status')}</HeaderCell>
            <Cell>
              {rowData => (
                <Tag color={getStatusColor(rowData._id?.status)}>
                  {t(rowData._id?.status?.toUpperCase())}
                </Tag>
              )}
            </Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('Created At')}</HeaderCell>
            <Cell>{rowData => new Date(rowData._id?.createdAt).toLocaleString()}</Cell>
          </Column>

          <Column width={70} fixed="right">
            <HeaderCell>{t('Action')}</HeaderCell>
            <Cell>
              {rowData => (
                <IconButton
                  size="xs"
                  icon={<MdOutlineDeleteOutline color="red" />}
                  onClick={() => handleDelete(rowData)}
                  id={`delete-${rowData._id._id}`}
                />
              )}
            </Cell>
          </Column>
        </Table>
        <div style={{ padding: 20 }}>
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={['total', '-', 'limit', '|', 'pager', 'skip']}
            total={pagination?.total || filteredData.length}
            limitOptions={[10, 20, 30, 50]}
            limit={limit}
            activePage={page}
            onChangePage={setPage}
            onChangeLimit={handleChangeLimit}
          />
        </div>
      </>

      <DrawerView
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        onFiltersChange={handleFiltersChange}
        onResetFilters={handleResetFilters}
        currentFilters={filters}
        users={users}
        usersLoading={usersLoading}
        toaster={toaster}
      />

      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>{t('Delete Third-Party Site Request')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t(
            'Are you sure you want to delete this third-party site request? This action cannot be undone.'
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowDeleteModal(false)} appearance="subtle">
            {t('Cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} appearance="primary" color="red">
            {t('Delete')}
          </Button>
        </Modal.Footer>
      </Modal>

      <AuthorizationPopup
        open={showAuthorizationPopup}
        onClose={() => setShowAuthorizationPopup(false)}
        name="thirdpartysite"
        settingsId={settingsId}
        initialAutoAccept={initialAutoAccept}
      />
    </>
  );
};

export default DataTable;
